<template>
  <section class="flex flex-col gap-5">
    <form @submit.prevent="submit()" class="grid grid-cols-12 gap-8 text-black">
      <div class="flex justify-between items-center col-span-12 mb-5">
        <h5>
          {{
            $t("labels.generic", {
              pt: "Selecionar usuários",
              en: "Select users",
            })
          }}
        </h5>
        <div class="flex items-center gap-3">
          <Button
            id="back-class-btn"
            :label="$t('labels.back')"
            class="btn-outline p-button-sm"
            @click="backStep"
          />
          <Button
            id="save-class-btn"
            :label="$t('labels.save')"
            type="submit"
            class="btn-primary p-button-sm"
            :loading="loading"
            :disabled="loading"
          />
        </div>
      </div>

      <div
        :class="`bg-white col-span-12 rounded-lg ${
          v$.selected.$error ? 'border border-red-500 overflow-hidden' : ''
        }`"
      >
        <div class="flex justify-between items-center p-4 border">
          <div v-if="filters.isCleareable" class="flex items-center gap-3">
            <Button
              label="Limpar"
              icon="pi pi-times"
              class="btn-outline p-button-sm"
              @click="clearFilter()"
            />
          </div>
          <div class="flex items-center gap-3">
            <Checkbox
              v-model="selectAll"
              @change="selectAllUsers()"
              :binary="true"
            />
            <p class="hidden md:flex">
              {{
                $t("labels.generic", {
                  pt: "Selecionar toda a página",
                  en: "Select all page",
                })
              }}
            </p>
          </div>
        </div>

        <DataTable :value="users" :loading="loading" showGridlines>
          <template #empty>
            <p class="text-gray-400 text-center p-3">
              {{
                $t("labels.generic", {
                  pt: "Dados não encontrados",
                  en: "Data not found",
                })
              }}
            </p>
          </template>
          <Column v-for="col of columns" :key="col.value" :field="col.value">
            <template #header>
              <div class="md:flex items-center gap-3">
                <span class="header-name-table">{{ col.name }}</span>
                <i
                  v-if="col.filter"
                  :class="`pi pi-filter text-white cursor-pointer hover:scale-125`"
                  @click="toggleFilter($event, col.value)"
                ></i>
              </div>
            </template>

            <template #body="slotProps">
              <div class="flex gap-3 items-center">
                <Checkbox
                  v-if="col.value === 'name'"
                  v-model="selected"
                  :inputId="slotProps.data.id"
                  :value="slotProps.data.id"
                  class="col-span-2 checkbox-secondary"
                />
                <label
                  :for="slotProps.data.id"
                  class="body-name-table hover:opacity-80 cursor-pointer"
                >
                  {{ slotProps.data[col.value] }}
                </label>
              </div>
            </template>
          </Column>
        </DataTable>

        <div
          v-if="courseParams?.total! > courseParams?.size"
          class="flex justify-end items-center"
        >
          <Paginator
            :rows="courseParams?.size"
            :totalRecords="courseParams?.total"
            :rowsPerPageOptions="[10, 20, 30]"
            @page="paginate($event)"
          ></Paginator>
        </div>
      </div>
      <Errors class="col-span-12" :errors="v$.selected.$errors" />
    </form>
  </section>

  <OverlayPanel ref="filter" style="min-width: 200px">
    <form @submit.prevent="filterFor">
      <p class="mb-2 text-sm font-bold">
        {{
          $t("labels.generic", {
            pt: "Pesquisar por:",
            en: "Search for:",
          })
        }}
      </p>
      <div v-if="filterType == 'name'" class="flex gap-2">
        <InputText v-model="courseParams.name" />
        <Button icon="pi pi-search" class="btn-primary !w-16" type="submit" />
      </div>
      <div v-if="filterType == 'login'" class="flex gap-2">
        <InputText v-model="courseParams.email" />
        <Button icon="pi pi-search" class="btn-primary !w-16" type="submit" />
      </div>
    </form>
  </OverlayPanel>
</template>

<script lang="ts">
import { required } from "@/locale/locale.config";
import { QueryParams } from "@/models/query-params.model";
import { Status } from "@/models/user.model";
import { ClassesRest } from "@/services/rest/classes.rest";
import { UsersRest } from "@/services/rest/users.rest";
import { GoToRoute } from "@/utils/go-to.util";
import { getItemSessionStorage } from "@/utils/save-storage.util";
import useVuelidate from "@vuelidate/core";
import { defineComponent, PropType } from "vue";
import { columnsSelect } from "../../classes.utils";
import { GenericsUtils, selectAll } from "@/utils/generics.util";

export default defineComponent({
  name: "courseContent",
  emits: ["onBack", "onSave", "onAllow"],
  props: {
    classroom: {
      type: Object as PropType<any>,
    },
  },
  computed: {
    rest(): ClassesRest {
      return new ClassesRest();
    },
    usersRest(): UsersRest {
      return new UsersRest();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
  },
  data() {
    return {
      data: {} as any,
      params: {
        id: this.$route.params.id ?? "",
        tab: 0,
      } as any,
      courseParams: new QueryParams(0, 10),
      columns: columnsSelect as any,
      loading: true as boolean,
      users: [] as any,
      searchFor: "" as string,
      filters: {
        isCleareable: false,
        name: null,
      } as any,
      filterType: "" as string,
      selected: this.classroom.users as any[],
      selectAll: false as any,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      selected: { required },
    };
  },
  mounted() {
    this.usersPaginated();
    this.loading = false;
  },
  methods: {
    submit(): void {
      this.v$.selected.$touch();
      if (!this.v$.selected.$error) {
        let oldData = JSON.parse(getItemSessionStorage("classData"));
        const data = {
          id: this.$route.params.id ?? undefined,
          name: oldData.name,
          coursesIds: oldData.coursesIds,
          addedCoursesIds: oldData.addedCoursesIds,
          removedCoursesIds: oldData.removedCoursesIds,
          studentsIds: this.$route.params.id
            ? undefined
            : this.selected.filter((obj, index) => {
                return index === this.selected.findIndex((e) => obj === e);
              }),
          addedStudentsIds: !this.$route.params.id
            ? undefined
            : this.selected.filter((e) => !oldData.users.includes(e)),
          removedStudentsIds: !this.$route.params.id
            ? undefined
            : oldData.users.filter((e) => !this.selected.includes(e)),
        };
        this.loading = true;
        if (this.$route.params.id) this.update(data);
        else this.create(data);
      }
    },
    create(data: any): void {
      this.rest.addClass(data).subscribe({
        next: () => {
          this.go.redirectToPage("classes");
        },
        error: (error) => {
          console.error(error);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
    },
    update(data: any): void {
      this.rest.updateClass(String(this.$route.params.id), data).subscribe({
        next: () => {
          this.go.redirectToPage("classes");
        },
        error: (error) => {
          console.error(error);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
    },
    paginate(event: { page: number; rows: number }): void {
      this.courseParams.page = event.page;
      this.courseParams.size = event.rows;
      this.selectAll = false;
      this.setPage();
    },
    setPage(): void {
      this.loading = true;
      this.usersRest.getUsers(this.courseParams).subscribe({
        next: (response) => {
          if (!response) return;
          this.users = response.content;
          this.courseParams.total = response.total;
          this.loading = false;
        },
      });
    },
    usersPaginated(): void {
      this.courseParams.role = "STUDENT";
      this.courseParams.status = Status.ACTIVATED;
      this.setPage();
    },
    toggleFilter(event, type): void {
      (this.$refs["filter"] as any).toggle(event);
      this.filterType = type;
    },
    filterFor(): void {
      this.filters.isCleareable = this.isCleareable();
      this.setPage();
      (this.$refs["filter"] as any).hide();
    },
    clearFilter(): void {
      this.filters.isCleareable = false;
      this.courseParams.name = "";
      this.courseParams.email = "";
      this.courseParams.page = 0;
      this.usersPaginated();
    },
    isCleareable(): boolean {
      return this.filters.name !== "";
    },
    backStep(): void {
      this.$emit("onBack", 0);
      this.go.redirectToPage("formClasses", this.params);
    },
    selectAllUsers(): void {
      this.selected = selectAll(this.selectAll, this.users, this.selected);
    },
  },
});
</script>
